/**
 * Native platform abstractions — Capacitor bridge.
 *
 * The frontend runs identically in three contexts:
 *   1. Regular browser (Chrome/Safari/Firefox)          → falls back to Web APIs
 *   2. Installed PWA (add-to-home-screen)               → same as (1)
 *   3. Native shell (Capacitor iOS/Android app)         → routes through native
 *
 * All helpers below detect the platform at call-time so components can be written
 * once and behave gracefully everywhere.
 */
import { Capacitor } from '@capacitor/core';

/** True when running inside the Capacitor-wrapped app (iOS/Android). */
export const isNative = () => Capacitor.isNativePlatform();

/** 'ios' | 'android' | 'web' */
export const platform = () => Capacitor.getPlatform();

// ────────────────────────────────────────────────────────────── SHARE

/**
 * Share a URL / text using the platform-native share sheet.
 * On web (or when Web Share API is unavailable), falls back to copying to clipboard.
 * Returns true if the share was completed, false if it was cancelled.
 */
export async function nativeShare({ title, text, url }) {
  const payload = { title, text, url };
  if (isNative()) {
    const { Share } = await import('@capacitor/share');
    try {
      const res = await Share.share(payload);
      return Boolean(res?.activityType || res?.completed !== false);
    } catch {
      return false;
    }
  }
  // Progressive-enhancement: Web Share API on modern browsers
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share(payload);
      return true;
    } catch {
      return false;
    }
  }
  // Final fallback: copy the URL to the clipboard
  if (typeof navigator !== 'undefined' && navigator.clipboard && url) {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

// ────────────────────────────────────────────────────────── QR SCANNER

/**
 * Trigger the native QR / barcode scanner. Returns the decoded string or null.
 * Only meaningful on native — web callers should hide the button.
 */
export async function scanQRCode() {
  if (!isNative()) return null;
  const { BarcodeScanner } = await import('@capacitor-mlkit/barcode-scanning');
  // Ensure Google's on-device ML Kit module is available (Android auto-downloads
  // it on first use; iOS bundles it).
  const supported = await BarcodeScanner.isSupported();
  if (!supported?.supported) return null;
  const perm = await BarcodeScanner.requestPermissions();
  if (perm.camera !== 'granted' && perm.camera !== 'limited') return null;
  const { barcodes } = await BarcodeScanner.scan({
    formats: ['QR_CODE'],
  });
  return barcodes?.[0]?.rawValue || null;
}

// ─────────────────────────────────────────────────────── PUSH NOTIFICATIONS

/**
 * Register the device with the OS push service (APNs on iOS, FCM on Android)
 * and return the device token so the backend can store it against the user.
 * Returns null on web or if the user denies permission.
 *
 * The backend endpoint that consumes this token is not yet wired — see
 * /app/backend/routers/push_notifications.py (to be created).
 */
export async function registerPushNotifications({ onRegister, onNotification } = {}) {
  if (!isNative()) return null;
  const { PushNotifications } = await import('@capacitor/push-notifications');

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return null;

  return new Promise((resolve) => {
    // Called once with the platform-specific device token
    PushNotifications.addListener('registration', (token) => {
      if (onRegister) onRegister(token.value);
      resolve(token.value);
    });
    PushNotifications.addListener('registrationError', () => resolve(null));
    if (onNotification) {
      PushNotifications.addListener('pushNotificationReceived', onNotification);
      PushNotifications.addListener('pushNotificationActionPerformed', onNotification);
    }
    PushNotifications.register();
  });
}

/** Convenience helper for the "share this event" button on Events pages. */
export async function shareEvent(evt, locale = 'fr') {
  const t = evt?.title || {};
  const title =
    (typeof t === 'object' ? t[locale] || t.fr || t.en : t) || 'Événement';
  const url =
    (typeof window !== 'undefined' ? window.location.origin : 'https://prieure-saint-leonard.fr') +
    `/evenements#${evt?.id || ''}`;
  return nativeShare({ title, text: title, url });
}
